<template>
  <div class="stock-source-controls">
    <div
      v-if="sourceList.length > 1"
      class="stock-source-controls__tabs"
      role="tablist"
    >
      <button
        v-for="(source, index) in sourceList"
        :key="source.value"
        type="button"
        role="tab"
        class="stock-source-controls__tab"
        :class="{ active: index === activeIndex }"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        @click="selectSource(index)"
      >
        {{ source.label }}
      </button>
    </div>

    <b-form-tags
      v-if="supportsTags"
      v-model="tags"
      class="stock-source-controls__tags"
      remove-on-delete
      no-outer-focus
      :input-attrs="{ list: tagListId }"
    >
      <template #default="{ tags: selectedTags, inputAttrs, inputHandlers, removeTag, addTag, disableAddButton }">
        <b-form-tag
          v-for="tag in selectedTags"
          :key="tag"
          class="stock-source-controls__tag"
          variant="secondary"
          @remove="removeTag(tag)"
        >
          {{ tag }}
        </b-form-tag>

        <div class="stock-source-controls__tags-field">
          <input
            v-bind="inputAttrs"
            class="stock-source-controls__tags-input"
            placeholder="Filter by tags"
            aria-label="Filter stock images by tag"
            v-on="inputHandlers"
          >

          <b-button
            v-if="!disableAddButton"
            size="sm"
            variant="outline-secondary"
            class="stock-source-controls__tags-add"
            @click="addTag()"
          >
            Add
          </b-button>
        </div>
      </template>
    </b-form-tags>
    <datalist :id="tagListId">
      <option
        v-for="tag in availableTags"
        :key="tag"
        :value="tag"
      />
    </datalist>
  </div>
</template>

<script>
import FileBrowserApi from '@frontend/services/api/file-browser';
import { SOURCE_TYPE_ITS_RAPID, SOURCES_WITH_TAGS } from '@frontend/constants/stock-image-source';

let tagListSeq = 0;

export default {
  name: 'StockSourceControls',
  props: {
    // JSON rather than an Array prop: custom-element attributes arrive as strings.
    sources: {
      type: String,
      default: '[]',
    },
  },
  data() {
    return {
      activeIndex: 0,
      tags: [],
      availableTags: [],
      tagsRequested: false,
      tagListId: `stock-tags-list-${(tagListSeq += 1)}`,
    };
  },
  computed: {
    sourceList() {
      try {
        const parsed = JSON.parse(this.sources);

        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },
    activeSource() {
      return this.sourceList[this.activeIndex]?.value || SOURCE_TYPE_ITS_RAPID;
    },
    supportsTags() {
      return SOURCES_WITH_TAGS.includes(this.activeSource);
    },
    tagsQuery() {
      return this.tags.join(',');
    },
  },
  watch: {
    tags() {
      this.emitChange();
    },
  },
  methods: {
    selectSource(index) {
      if (index === this.activeIndex) {
        return;
      }

      this.activeIndex = index;
      this.ensureTagOptions();

      // Otherwise a tag-less source keeps filtering by a control the user can no longer see.
      if (!this.supportsTags && this.tags.length) {
        this.tags = [];

        return;
      }

      this.emitChange();
    },

    emitChange() {
      this.$emit('change', { sourceIndex: this.activeIndex, tagsQuery: this.tagsQuery });
    },

    activate() {
      this.ensureTagOptions();
    },

    async ensureTagOptions() {
      if (!this.supportsTags || this.tagsRequested) {
        return;
      }

      this.tagsRequested = true;

      try {
        const { data } = await FileBrowserApi.fetchStockTags();
        this.availableTags = Array.isArray(data) ? data : [];
      } catch (error) {
        this.availableTags = [];
      }
    },
  },
};
</script>

<style scoped>
.stock-source-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.stock-source-controls__tabs {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* The root still carries `.form-control`, which would draw a box around the badges too. */
.stock-source-controls__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  width: auto;
  margin-left: auto;
  padding: 0;
  border: 0;
  background-color: transparent;
  box-shadow: none;
}

/* Slot content is rendered by b-form-tags, so it does not reliably carry this file's scope id. */
.stock-source-controls ::v-deep .stock-source-controls__tags-field {
  display: flex;
  align-items: center;
  gap: 6px;
  /* Same track as `.select-image-toolbar__search`, which sits next to it in the toolbar. */
  flex: 0 0 224px;
  height: 36px;
  padding: 0 4px 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: #FFFFFF;
}

.stock-source-controls ::v-deep .stock-source-controls__tags-field:focus-within {
  border-color: #16a38b;
  box-shadow: 0 0 0 3px rgba(22, 163, 139, 0.15);
}

.stock-source-controls ::v-deep .stock-source-controls__tags-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  font-family: "Nunito", sans-serif;
  font-size: 14px;
  color: #0f172a;
}

/* #b1b1b1 is $greyscale200; a scoped block cannot reach the SCSS token. */
.stock-source-controls ::v-deep .stock-source-controls__tags-input::placeholder {
  color: #b1b1b1;
  font-family: "Nunito", sans-serif;
  font-size: inherit;
  font-weight: 500;
}

.stock-source-controls ::v-deep .stock-source-controls__tags-input:focus {
  outline: none;
  box-shadow: none;
}

/* Chrome draws a dropdown arrow inside any input carrying `list`, mid-field next to Add. */
.stock-source-controls ::v-deep .stock-source-controls__tags-input::-webkit-calendar-picker-indicator {
  display: none;
  opacity: 0;
  width: 0;
  height: 0;
  -webkit-appearance: none;
  appearance: none;
}

.stock-source-controls ::v-deep .stock-source-controls__tags-add {
  flex: 0 0 auto;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 18px;
}

.stock-source-controls__tab {
  padding: 6px 12px;
  line-height: 20px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: #64748b;
  white-space: nowrap;
}

.stock-source-controls__tab:hover {
  background-color: #f0f2f4;
  color: rgb(34, 38, 42);
}

.stock-source-controls__tab.active {
  background-color: rgba(22, 163, 139, 0.12);
  color: #16a38b;
}
</style>
